import { useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';

const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w.charAt(0)).join('').toUpperCase() || '·';

/** A round photo, or the person's initials when there is no photo. */
export function Avatar({ src, name, size = 32, className = '' }: { src?: string | null; name: string; size?: number; className?: string }) {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.38)) };
  if (src && !broken) return <img className={`av av-img ${className}`} src={src} alt="" width={size} height={size} style={style} onError={() => setBroken(true)} />;
  return <span className={`av ${className}`} style={style} aria-hidden="true">{initialsOf(name)}</span>;
}

/** Upload, replace or remove a profile photo. The caller does the network calls. */
export function PhotoUpload({ src, name, upload, remove, hint = 'JPEG, PNG or WebP, up to 5 MB.' }: {
  src?: string | null; name: string; upload: (f: File) => Promise<void>; remove: () => Promise<void>; hint?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  async function pick(f: File | undefined) {
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) { setErr('Use a JPEG, PNG or WebP photo.'); return; }
    if (f.size > 5 * 1024 * 1024) { setErr('The photo must be 5 MB or smaller.'); return; }
    setErr(''); setBusy(true); setPreview(URL.createObjectURL(f));
    try { await upload(f); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not upload the photo.'); setPreview(null); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  }
  return (
    <div className="photo-up">
      <button type="button" className="photo-up-av" onClick={() => input.current?.click()} aria-label="Change profile photo" disabled={busy}>
        <Avatar src={preview ?? src} name={name} size={72} />
        <span className="photo-up-cam" aria-hidden="true"><Camera /></span>
      </button>
      <div className="photo-up-t">
        <b>Profile photo</b>
        <small>{hint}</small>
        {err && <small className="photo-up-err" role="alert">{err}</small>}
        <div className="photo-up-a">
          <button type="button" className="btn ghost sm" disabled={busy} onClick={() => input.current?.click()}><Camera />{busy ? 'Uploading…' : src || preview ? 'Change photo' : 'Upload photo'}</button>
          {(src || preview) && <button type="button" className="link" disabled={busy} onClick={async () => { setBusy(true); try { await remove(); setPreview(null); } catch (e) { setErr(e instanceof Error ? e.message : 'Could not remove the photo.'); } finally { setBusy(false); } }}><Trash2 />Remove</button>}
        </div>
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => void pick(e.target.files?.[0])} />
    </div>
  );
}
